import type { ClientIndustry, ClientStatus } from '@veem/schema'
import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'

/**
 * One «Клієнт» in the interface, two records underneath, exactly as ADR 0004 models them:
 * a company (`clients`) and people (`contacts`) joined by a link that carries the person's role.
 * The UI only stops making the user think about that split.
 *
 * `kind` is new. The API can infer a legal entity (8-digit ЄДРПОУ) from a ФОП (10-digit ІПН),
 * but a private person who buys once has no code at all, so it needs to be stored.
 */
export type CustomerKind = 'company' | 'fop' | 'person'

export const CUSTOMER_KIND_LABELS: Record<CustomerKind, string> = {
  company: 'Компанія',
  fop: 'ФОП',
  person: 'Приватна особа',
}

export interface ProtoCustomer {
  version?: number
  id: string
  kind: CustomerKind
  name: string
  edrpou: string | null
  industry: ClientIndustry
  city: string
  address: string | null
  status: ClientStatus
  ownerId: string
  sellerEntityIds: string[]
  phone: string | null
  email: string | null
  website: string | null
  note: string | null
  categoryId: string | null
  createdAt: string
}

/** A customer category, or a subcategory when `parentId` is set. */
export interface CustomerCategory {
  id: string
  version?: number
  name: string
  parentId: string | null
  archived: boolean
}

export interface ProtoPerson {
  id: string
  fullName: string
  phone: string | null
  email: string | null
}

export interface CustomerLink {
  customerId: string
  personId: string
  role: string
  note?: string | null
  primary: boolean
}

export const CUSTOMERS: ProtoCustomer[] = createMutable([] as ProtoCustomer[])

export const CUSTOMER_CATEGORIES: CustomerCategory[] = createMutable([] as CustomerCategory[])

export const PERSONS: ProtoPerson[] = createMutable([] as ProtoPerson[])

/** Олена Кривенко also consults for Еко-Бус: one person, two roles, no duplicate card. */
export const LINKS: CustomerLink[] = createMutable([] as CustomerLink[])

export const customerById = (id: string): ProtoCustomer => {
  const customer = CUSTOMERS.find((candidate) => candidate.id === id)
  if (!customer) throw new Error(`Unknown prototype customer ${id}`)
  return customer
}

/** People linked to a customer, primary first, in the shape the deal record carries. */
export const contactsOf = (customerId: string, links = LINKS, persons = PERSONS) =>
  links
    .filter((link) => link.customerId === customerId)
    .sort((left, right) => Number(right.primary) - Number(left.primary))
    .flatMap((link) => {
      const person = persons.find((candidate) => candidate.id === link.personId)
      return person ? [{ ...person, role: link.role, primary: link.primary }] : []
    })
